import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import styles from './notificationPopup.module.scss';
import { BellOutlined } from '@ant-design/icons';
import InAppNotificationService from 'services/InAppNotificationService/inAppNotification.service';
import InAppNotificationCard from './InAppNotificationCard';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import { InAppNotification } from 'models/InAppNotification/inAppNotification.model';
import { deserialize } from 'serializr';
import { SocketContext } from 'context/SocketContext';

interface NotificationPopupProps {
  fetchUnreadCount: () => void;
}

const NotificationPopup: FC<NotificationPopupProps> = props => {
  const {
    fetchInAppNotifications,
    loading,
    inAppNotifications,
    markAsRead,
    markAllAsread,
    inAppNotificationsPagination,
  } = InAppNotificationService();
  const { fetchUnreadCount } = props;
  const [inAppNotificationList, setInAppNotificationList] = useState<
    InAppNotification[]
  >([]);

  useEffect(() => {
    fetchInAppNotifications();
  }, []);

  const removeDuplicate = (arrayList: InAppNotification[]) => {
    return arrayList.reduce(
      (accumulator: InAppNotification[], current: InAppNotification) => {
        const duplicate = accumulator.find(
          (item: InAppNotification) => item?.id === current?.id
        );
        if (!duplicate) {
          accumulator.push(current);
        }
        return accumulator;
      },
      []
    );
  };

  useEffect(() => {
    if (inAppNotifications) {
      const uniqueList = removeDuplicate(inAppNotifications);
      setInAppNotificationList(JSON.parse(JSON.stringify(uniqueList)));
    }
  }, [inAppNotifications]);

  const { socket } = useContext(SocketContext);

  useEffect(() => {
    if (socket) {
      socket.on('notification', (socketData: unknown) => {
        const data = deserialize(
          InAppNotification,
          socketData
        ) as InAppNotification;
        setInAppNotificationList(prevMessages => {
          prevMessages.unshift(data);

          // Prevent duplicate messages due to the socket
          const uniqueMessages = prevMessages.reduce(
            (accumulator: InAppNotification[], current: InAppNotification) => {
              const duplicate = accumulator.find(
                (item: InAppNotification) => item?.id === current.id
              );
              if (!duplicate) {
                accumulator.push(current);
              }
              return accumulator;
            },
            []
          );

          return uniqueMessages;
        });
      });
      return () => {
        socket.off('notification', () => {});
      };
    }
  }, []);

  const popoverRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = () => {
    if (!inAppNotificationsPagination) return;
    const { totalPages, currentPage } = inAppNotificationsPagination;

    if ((currentPage ?? 0) > (totalPages ?? 0)) return;

    const scrollTop = popoverRef?.current?.scrollTop ?? 0;
    const scrollHeight = popoverRef?.current?.scrollHeight ?? 0;
    const clientHeight = popoverRef?.current?.clientHeight ?? 0;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      // Load more data
      !loading &&
        fetchInAppNotifications({
          page: (currentPage ?? 0) + 1,
          append: true,
        });
    }
  };

  const markItemAsRead = async (notificationId: string) => {
    await markAsRead(notificationId);
    fetchUnreadCount();
    const updatedData = inAppNotificationList.map(notification => {
      if (notification.id === notificationId) {
        notification.isRead = true;
      }
      return notification;
    });
    setInAppNotificationList(updatedData);
  };

  const markAllItemAsRead = async () => {
    await markAllAsread();
    fetchUnreadCount();
    const updatedData = inAppNotificationList.map(notification => {
      notification.isRead = true;
      return notification;
    });
    setInAppNotificationList(updatedData);
  };

  return (
    <div className={styles.notificationPopup}>
      <div className={styles.header}>
        <span className={styles.title}>
          <BellOutlined /> Notifications
        </span>
        <div className={styles.markAsRead} onClick={markAllItemAsRead}>
          Mark all as read
        </div>
      </div>
      <div
        className={styles.notificationBody}
        ref={popoverRef}
        onScroll={handleScroll}>
        {inAppNotificationList.length < 1 && !loading && (
          <p>You don't have any notifications yet</p>
        )}
        {inAppNotificationList.map((notification, index) => (
          <InAppNotificationCard
            key={index}
            notification={notification}
            markAsRead={markItemAsRead}
          />
        ))}
        {loading && (
          <div className={styles.loaderContainer}>
            <LoaderAnimation />
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPopup;
